import type { AgentAttachment } from '@multiplayer-app/ai-agent-types';

export interface FileValidationConfig {
  maxFileSize: number;
  maxTotalAttachmentsSize: number;
  maxFilesPerMessage: number;
  allowedMimeTypes?: string[];
}

export interface FileValidationError {
  type: 'size' | 'type' | 'count' | 'totalSize';
  message: string;
  fileName?: string;
}

/**
 * Validates a single file against configuration
 */
export function validateFile(
  file: File,
  config: FileValidationConfig
): FileValidationError | null {
  // Check file size
  if (file.size > config.maxFileSize) {
    return {
      type: 'size',
      message: `File "${file.name}" exceeds maximum size of ${formatFileSize(config.maxFileSize)}`,
      fileName: file.name,
    };
  }

  // Check mime type if restrictions are set
  if (config.allowedMimeTypes && config.allowedMimeTypes.length > 0) {
    const isAllowed = config.allowedMimeTypes.some((pattern) => {
      if (pattern.endsWith('/*')) {
        // Wildcard pattern like "image/*"
        const baseType = pattern.slice(0, -2);
        return file.type.startsWith(baseType);
      }
      return file.type === pattern;
    });

    if (!isAllowed) {
      return {
        type: 'type',
        message: `File type "${file.type}" is not allowed for "${file.name}"`,
        fileName: file.name,
      };
    }
  }

  return null;
}

/**
 * Validates multiple files and checks aggregate limits
 */
export function validateFiles(
  files: File[],
  existingAttachments: AgentAttachment[],
  config: FileValidationConfig
): FileValidationError | null {
  // Check file count
  const totalFiles = files.length + existingAttachments.filter(
    (a) => a.type === 'file'
  ).length;
  if (totalFiles > config.maxFilesPerMessage) {
    return {
      type: 'count',
      message: `Maximum ${config.maxFilesPerMessage} files per message allowed`,
    };
  }

  // Validate each file
  for (const file of files) {
    const error = validateFile(file, config);
    if (error) return error;
  }

  // Check total size
  const existingSize = existingAttachments
    .filter((a) => a.type === 'file')
    .reduce((sum, a) => {
      const size = (a.metadata as any)?.size || (a as any).size || 0;
      return sum + size;
    }, 0);

  const newFilesSize = files.reduce((sum, f) => sum + f.size, 0);
  const totalSize = existingSize + newFilesSize;

  if (totalSize > config.maxTotalAttachmentsSize) {
    return {
      type: 'totalSize',
      message: `Total attachment size exceeds maximum of ${formatFileSize(config.maxTotalAttachmentsSize)}`,
    };
  }

  return null;
}

/**
 * Formats file size in human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Gets default file validation config
 */
export function getDefaultFileValidationConfig(): FileValidationConfig {
  return {
    maxFileSize: 10 * 1024 * 1024, // 10MB
    maxTotalAttachmentsSize: 50 * 1024 * 1024, // 50MB
    maxFilesPerMessage: 10,
    allowedMimeTypes: undefined, // All types allowed by default
  };
}
